import type { WrittenPage } from '../../sections'
import { PDF } from '../ebook-transform-better-playbook'

/**
 * The Transform Better Playbook — Türkçe.
 *
 * Bölüm sırası, görseller ve maddeler İngilizcesiyle birebir aynı; değişen
 * yalnızca nasıl söylendiği. E-kitabın adı kapağında basılı olduğu için
 * İngilizce kalıyor; "playbook", "governance", "case study" ve
 * "#transformbetter" da öyle — bunlar yerleşik terimler ve markanın kendi
 * sözleri. Şirket adları olduğu gibi kalıyor.
 *
 * İndirilen dosya İngilizce — playbook'un Türkçesi yok. Bu, sayfayı Türkçe
 * yazmamak için bir sebep değil: sayfayı okuyup indirme kararını veren kişi
 * Türkiye'deki okur, ve karar Türkçe veriliyor.
 *
 * Formla ilgili açık soru İngilizce sayfada duruyor ve orada bir kez duruyor:
 * `check` iki dilde tekrarlanınca aynı karar iki not olarak sayılıyor.
 */
export const ebookTransformBetterPlaybook: WrittenPage = {
  navLabel: 'The Transform Better Playbook',
  title: 'Transform Better: Başarılı dönüşümün stratejileri',
  description:
    'Değişimin tek sabit olduğu bir dünyada rekabette kalmak dönüşümü benimsemekle mümkün. '
    + 'Digitopia Impact Labs’in Transform Better Playbook’unu indirin.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Başarılı dönüşümün stratejileri',
      headline: 'Transform Better',
      body: [
        'Değişimin tek sabit olduğu bir dünyada rekabette kalmanın yolu dönüşümü benimsemekten '
          + 'geçiyor. İş dönüşümünün karmaşıklığında yolunuzu nasıl bulacağınızı ve bu işi nasıl '
          + 'ustalıkla yürüteceğinizi temel rehberimizde keşfedin: “The Transform Better Playbook: '
          + 'Strategies for Successful Transformation.”',
      ],
      ctas: [{ label: 'İndirin', href: PDF }],
      photo: {
        src: '/photo/transform-better-playbook-ebook-artwork.webp',
        alt: 'Playbook’un kapak görseli: karanlığa doğru katlanan magenta ve mor ışık sıraları.',
      },
      photoFrame: 'square',
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Bu playbook’u neden indirmelisiniz?',
      points: [
        'Dönüşümün neden kritik olduğunu anlayın: değişimin ana sürükleyicilerini ve uyum '
          + 'sağlamanın ayakta kalmak ve büyümek için neden şart olduğunu görün.',
        'Neyin dönüşmesi gerektiğini keşfedin: stratejik hedeflerinizle hizalanan yeni iş '
          + 'modellerini, operasyonel yapıları ve governance çerçevelerini inceleyin.',
        'Dönüşümü nasıl başaracağınızı öğrenin: etkili değişim yönetimi, liderlik ve inovasyon '
          + 'kültürü üzerine uygulanabilir içgörüler edinin.',
      ],
      ctas: [{ label: 'Playbook’u indirin', href: PDF }],
      photo: {
        src: '/photo/transform-better-playbook-ebook-covers.webp',
        alt:
          'The Transform Better Playbook’un biri diğerinin arkasında duran iki kopyası, beyaz '
          + 'zeminde.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Elinize ne geçecek?',
      points: [
        'Uygulanabilir stratejiler: dönüşümü hayata geçirmek için adım adım, net bir yol.',
        'Gerçek vaka çalışmaları: Netflix, Amazon ve LEGO gibi sektör liderlerinin başarılarından '
          + 'öğrenin.',
        'Başarının kritik faktörleri: sürdürülebilir başarı için denge, ölçüm ve dönüşümü '
          + 'insanileştirmek.',
      ],
      ctas: [{ label: 'Playbook’u indirin', href: PDF }],
      photo: {
        src: '/photo/transform-better-playbook-ebook-stack.webp',
        alt: 'The Transform Better Playbook’un basılı kopyalarından bir yığın, yukarıdan görünüyor.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Neden Digitopia?',
      sub:
        'Digitopia’da başarınız bizim işimiz. #transformbetter yaklaşımımız yolun her adımında '
        + 'yanınızda güvendiğiniz bir partner olmasını, yolculuğunuz için gereken uzmanlığın ve '
        + 'araçların elinizin altında durmasını sağlıyor.',
      ctas: [{ label: 'Playbook’u indirin', href: PDF }],
      photo: {
        src: '/photo/digital-vitality-concept.webp',
        alt:
          'Digital Vitality Concept: müşteri, operasyon, teknoloji, governance, insan ve inovasyon '
          + 'olmak üzere altı boyutlu bir çark; hücum yarısı digital velocity’yi, defans yarısı '
          + 'digital endurance’ı mümkün kılıyor.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Bu fırsatı kaçırmayın!',
      sub:
        'İşinizi geleceğe hazırlama fırsatını kaçırmayın. Değişimi güvenle karşılayın ve stratejik '
        + 'vizyonu somut sonuçlara çevirin.',
      cta: { label: 'Playbook’u indirin', href: PDF },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'Müşterilerimiz ne diyor?',
      items: [
        {
          quote:
            'Digitopia’nın Digital Maturity Index’i dijital dönüşümümüzü yapılandırmanın kilit '
            + 'parçası oldu. Nerede durduğumuza ve hem holding içinde hem sektörde nasıl '
            + 'konumlandığımıza dair net, nesnel bir görüntü verdi. Daha da önemlisi, ilerlememizi '
            + 'zaman içinde takip etmemizi ve dijital yatırımlarımızı ölçülebilir sonuçlarla '
            + 'hizalamamızı sağladı. Bu yapılandırılmış yaklaşım dönüşüm çalışmalarımıza netlik, '
            + 'odak ve güven getiriyor.',
          source: 'Erdinç Uğurlu, CIO, TEMSA',
          company: 'TEMSA',
          logo: { name: 'TEMSA', src: '/logo/temsacard.png' },
          portrait: { src: '/photo/portrait-erdinc-ugurlu.png', alt: 'Erdinç Uğurlu' },
        },
      ],
    },
  ],
}
